import { useNavigation, useRoute } from "@react-navigation/core";
import { useHeaderHeight } from "@react-navigation/elements";
import React, { Fragment, memo, useEffect } from "react";
import {
	ActivityIndicator,
	ScrollView,
	Text,
	TouchableOpacity,
	View
} from "react-native";
import { CheckBox } from "react-native-elements";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useDispatch, useSelector } from "react-redux";
import { fetchPublishRequest } from "../actions/template";
import {
	CLEAR_EQUIPMENT,
	CLEAR_PUBLISH,
	CLEAR_PUBLISH_DATE,
	SET_DROP_DOWN_ALERT_WARN,
	SET_PUBLISH_CHOICE,
	SET_PUBLISH_DATE,
	SET_PUBLISH_EQUIPMENT,
	SET_PUBLISH_TEMPLATE,
	SET_PUBLISH_TIME,
	SET_SHARE_CHANNEL_ID,
	SET_TEMPLATE_CHANNEL_ID
} from "../actions/types";
import CommonPicker from "../components/CommonPicker";
import LiveEvent from "../components/LiveEvent";
import { ROLE_GUEST, TYPE_TEMPLATE } from "../config/constants";
import { getTranslatedProperty, t } from "../services/i18n";
import commonStyles, { COLOR2, SCREEN_HEIGHT } from "../styles/commonStyles";

const SelectTemplateDate = () => {
	const dispatch = useDispatch();
	const navigation = useNavigation();
	const headerHeight = useHeaderHeight();
	const route = useRoute();
	const insets = useSafeAreaInsets();

	let {
		isFetching,
		dateId,
		timeId,
		equipmentId,
		ad,
		choiceId,
		locationId,
		templateDates,
		templateTimes,
		equipments,
		displayTimes,
		template,
		data,
		groupId,
		displayPreview,
		templateChannels,
		templateChannelId,
		shareChannels,
		shareChannelId
	} = useSelector(state => state.templateReducer);
	const { templates } = useSelector(state => state.entitiesReducer);
	const { user } = useSelector(state => state.profileReducer);

	useEffect(() => {
		if (!template) {
			if (route.params?.id) {
				template = templates[route.params.id];
				dispatch({
					type: SET_PUBLISH_TEMPLATE,
					payload: template
				});
			} else {
				navigation.goBack();
			}
		}
		navigation.setOptions({
			title: getTranslatedProperty(template, "name")
		});
		return () =>
			dispatch({
				type: CLEAR_PUBLISH
			});
	}, []);

	useEffect(() => {
		if (data) {
			// Must be `popTo`, not `navigate`. `navigate("TabScreen", {...})` in
			// React Navigation 7 merges params on the existing TabScreen entry
			// without popping the screens above it, so SelectTemplateDateScreen
			// stays mounted (with `data` still truthy in Redux) and the next
			// attempt reuses the stale instance — publish then no longer routes.
			// `popTo` guarantees the pop, which unmounts this screen and lets its
			// CLEAR_PUBLISH cleanup reset the reducer. Mirrors the mobile call.
			navigation.popTo("TabScreen", {
				screen: "LiveTab",
				params: {
					screen: "LiveScreen",
					params: { communityUri: route.params?.communityUri }
				}
			});
		}
	}, [data]);

	const sendRequest = () => {
		if (user) {
			if (user.role === ROLE_GUEST) {
				dispatch({
					type: SET_DROP_DOWN_ALERT_WARN,
					warn: "guestforbidden"
				});
			} else {
				dispatch(
					fetchPublishRequest({
						dateId,
						timeId,
						locationId,
						equipmentId,
						templateId: template._id,
						groupId,
						choiceId,
						goodDealId: ad?._id,
						...(route.params?.type === TYPE_TEMPLATE && templateChannelId
							? { channelsIds: [templateChannelId] }
							: shareChannelId
							? { channelsIds: [shareChannelId] }
							: {})
					})
				);
			}
		} else {
			dispatch({
				type: SET_DROP_DOWN_ALERT_WARN,
				warn: "nouserforbidden"
			});
		}
	};

	const {
		red,
		color5,
		bgColor1,
		bgWhite,
		flex1,
		font,
		fontBold,
		fs16,
		pv10,
		column,
		darkgrey,
		rounded5,
		borderBrown,
		mv10,
		h100p,
		row
	} = commonStyles;

	if (!template && route.params?.id) {
		template = templates[route.params.id];
	}

	const renderStickyButtons = () => {
		return (
			<View
				style={[
					row,
					{
						position: "absolute",
						bottom: 0,
						left: 0,
						right: 0,
						borderTopWidth: 1,
						borderTopColor: "#e0e0e0"
					}
				]}
			>
				<TouchableOpacity
					underlayColor="transparent"
					activeOpacity={0.8}
					onPress={() =>
						navigation.popTo("TabScreen", {
							screen: "LiveTab",
							params: {
								screen: "LiveScreen",
								params: { communityUri: route.params?.communityUri }
							}
						})
					}
					style={[
						{
							height: 60 + insets.bottom,
							flex: 1,
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: "white"
						}
					]}
				>
					<Text style={[fs16, fontBold, red]}>{t("button:cancel")}</Text>
				</TouchableOpacity>
				<TouchableOpacity
					underlayColor="transparent"
					activeOpacity={0.8}
					onPress={sendRequest}
					disabled={isFetching}
					style={[
						{
							height: 60 + insets.bottom,
							flex: 1,
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: COLOR2,
							borderLeftWidth: 1,
							borderLeftColor: "#e0e0e0",
							opacity: isFetching ? 0.6 : 1
						}
					]}
				>
					{isFetching ? (
						<ActivityIndicator size="small" color="white" />
					) : (
						<Text style={[fontBold, fs16, { color: "white" }]}>
							{t("button:post")}
						</Text>
					)}
				</TouchableOpacity>
			</View>
		);
	};

	const item = {
		groupId,
		event: {
			dateId,
			timeId,
			locationId,
			equipmentId,
			choiceId
		}
	};
	return (
		<View
			style={[
				bgColor1,
				column,
				h100p,
				{ height: SCREEN_HEIGHT - headerHeight }
			]}
		>
			{displayPreview && template && (
				<LiveEvent item={item} previewTemplate={template} />
			)}

			<ScrollView style={flex1} contentContainerStyle={{ paddingBottom: 90 }}>
				{template && template.choices && (
					<Fragment>
						<Text
							style={[
								font,
								color5,
								{ fontSize: 18, marginLeft: 10, marginTop: 15 }
							]}
						>
							{t("template:selectmessage")}
						</Text>
						<View style={{ paddingTop: 10, paddingHorizontal: 20 }}>
							<CommonPicker
								data={template.choices}
								selectedValue={choiceId || ""}
								onChange={itemValue =>
									dispatch({
										type: SET_PUBLISH_CHOICE,
										payload: itemValue
									})
								}
							/>
						</View>
					</Fragment>
				)}
				{template?.subtype !== "Post" && (
					<Fragment>
						<Text
							style={[
								font,
								color5,
								{ fontSize: 18, marginLeft: 10, marginTop: 15 }
							]}
						>
							{t("template:proposedate")}
						</Text>
						<View style={{ paddingTop: 10, paddingHorizontal: 20 }}>
							<CommonPicker
								data={templateDates}
								firstPickerLabel={t("template:proposedate")}
								selectedValue={dateId || ""}
								onChange={itemValue =>
									dispatch({
										type:
											itemValue === "undefined"
												? CLEAR_PUBLISH_DATE
												: SET_PUBLISH_DATE,
										payload: itemValue
									})
								}
							/>
							{displayTimes && (
								<View style={{ paddingTop: 10 }}>
									<CommonPicker
										data={templateTimes}
										selectedValue={timeId || ""}
										onChange={itemValue =>
											dispatch({
												type: SET_PUBLISH_TIME,
												payload: itemValue
											})
										}
									/>
								</View>
							)}
						</View>
					</Fragment>
				)}
				{equipments && template?.subtype !== "Post" && (
					<Fragment>
						<Text
							style={[
								font,
								color5,
								{ fontSize: 18, marginLeft: 10, marginTop: 15 }
							]}
						>
							{t("template:proposelocation")}
						</Text>
						<View style={[pv10, { paddingHorizontal: 20 }]}>
							<CommonPicker
								data={equipments}
								firstPickerLabel={t("template:proposelocation")}
								selectedValue={equipmentId}
								onChange={itemValue =>
									dispatch({
										type:
											itemValue === "undefined"
												? CLEAR_EQUIPMENT
												: SET_PUBLISH_EQUIPMENT,
										payload: itemValue
									})
								}
							/>
						</View>
					</Fragment>
				)}
				{((route.params?.type === TYPE_TEMPLATE && templateChannelId) ||
					(route.params?.type !== TYPE_TEMPLATE && shareChannelId)) && (
					<>
						<Text
							style={[
								font,
								color5,
								{ fontSize: 18, marginLeft: 10, marginTop: 15 }
							]}
						>
							{t("live:target")}
						</Text>
						<View
							style={[
								flex1,
								bgWhite,
								rounded5,
								borderBrown,
								mv10,
								{ marginHorizontal: 20 }
							]}
						>
							{(route.params?.type === TYPE_TEMPLATE
								? templateChannels
								: shareChannels
							).map(({ id, name }) => (
								<CheckBox
									key={id}
									title={name}
									textStyle={[darkgrey, font]}
									containerStyle={[flex1]}
									checkedColor={COLOR2}
									uncheckedColor={COLOR2}
									checkedIcon="dot-circle-o"
									uncheckedIcon="circle-o"
									checked={
										id ===
										(route.params?.type === TYPE_TEMPLATE
											? templateChannelId
											: shareChannelId)
									}
									onPress={() =>
										dispatch({
											type:
												route.params?.type === TYPE_TEMPLATE
													? SET_TEMPLATE_CHANNEL_ID
													: SET_SHARE_CHANNEL_ID,
											payload: id
										})
									}
								/>
							))}
						</View>
					</>
				)}
			</ScrollView>
			{renderStickyButtons()}
		</View>
	);
};

const MemoizedSelectTemplateDate = memo(SelectTemplateDate);
export default MemoizedSelectTemplateDate;
